// ==========================================================================
// i18n Configuration - Internationalization setup
// ==========================================================================
//
// English is compiled in and is the offline guarantee: the app always renders,
// even on a cold start with no network. Every other locale is served by the
// engine's unified catalog (ENG-949) and cached locally, so translators own
// copy changes without a frontend release. A cached catalog is applied
// synchronously at boot; a fresh one is fetched in the background.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { isSupportedLanguage, LANGUAGES, localeFromSearch, type Language } from './languages';
import en from './locales/en.json';
import { fetchCatalog, readCachedCatalog } from './remoteCatalog';

// Get saved language or detect from browser / Telegram client
const getSavedLanguage = (): Language => {
  // An explicit `?lang=` override (ENG-1061) wins over the stored/device
  // language, and is persisted so the preview keeps working after the root
  // redirect strips `?lang=`. Persist explicitly here rather than relying on
  // the languageChanged handler: with bundled resources i18next's init load
  // is synchronous, so the boot change can fire before the handler is
  // registered (and a same-locale change may not fire it at all). Best-effort,
  // since this runs at boot a localStorage throw (private mode) must not
  // white-screen the app.
  const fromUrl = localeFromSearch(window.location.search);
  if (fromUrl) {
    try {
      localStorage.setItem('language', fromUrl);
    } catch {
      /* storage unavailable — ignore */
    }
    return fromUrl;
  }

  const saved = localStorage.getItem('language');
  if (isSupportedLanguage(saved)) {
    return saved;
  }

  // When running inside a Telegram Mini-App, prefer the Telegram-reported
  // language_code over navigator.language. The pilot is Ethiopia so any
  // unrecognised Telegram language falls back to Amharic rather than
  // English. Read window.Telegram directly here — importing platform.ts
  // would create a boot-order import cycle.
  const tgUser = (
    window as { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } } } }
  ).Telegram?.WebApp?.initDataUnsafe?.user;
  if (tgUser?.language_code) {
    const tgLang = tgUser.language_code.split('-')[0];
    if (isSupportedLanguage(tgLang)) {
      return tgLang;
    }
    return 'am';
  }

  // Detect from browser
  const browserLang = navigator.language.split('-')[0];
  if (isSupportedLanguage(browserLang)) {
    return browserLang;
  }

  return 'en';
};

const initialLanguage = getSavedLanguage();

// Seed with English plus whatever locales are already cached, so the first
// paint is in the right language on every boot after the first.
const resources: Record<string, { translation: Record<string, unknown> }> = {
  en: { translation: en },
};

for (const locale of LANGUAGES) {
  if (locale === 'en') continue;

  const cached = readCachedCatalog(locale);
  if (cached) {
    resources[locale] = { translation: cached.resources };
  }
}

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: 'en',
  supportedLngs: LANGUAGES,

  interpolation: {
    escapeValue: false, // React already escapes
  },

  debug: false,
});

// Locales with a fetch in flight, so concurrent triggers (boot warm-up and the
// init-time languageChanged event) don't fire duplicate requests on the same
// low-bandwidth path.
const inFlight = new Set<Language>();

/**
 * Load a locale's catalog from the engine and hand it to i18next. Safe to call
 * repeatedly: the fetch revalidates with an ETag, and re-adding a bundle is how
 * i18next takes an update. English is skipped — it is bundled, never fetched.
 */
export async function loadCatalog(locale: Language): Promise<void> {
  if (locale === 'en' || inFlight.has(locale)) return;

  inFlight.add(locale);
  try {
    const resource = await fetchCatalog(locale);
    if (!resource) return;

    i18n.addResourceBundle(locale, 'translation', resource, true, true);

    if (i18n.language === locale) {
      // Re-emit so components rendered against the fallback re-render with the
      // freshly loaded copy.
      await i18n.changeLanguage(locale);
    }
  } finally {
    inFlight.delete(locale);
  }
}

// Warm the active locale on boot. Cached copy (if any) is already rendering,
// so this only refreshes it; a failure leaves the app working as-is.
void loadCatalog(initialLanguage);

// Save language preference when changed, and revalidate the catalog. Always
// reload — not only when the bundle is absent: a locale seeded from cache at
// boot has a bundle already, but a week-old cached copy still needs its ETag
// checked, or translators' updates would never reach users who booted in a
// different language. loadCatalog revalidates cheaply (304) and no-ops for en
// and for an in-flight locale.
i18n.on('languageChanged', (lng) => {
  localStorage.setItem('language', lng);
  document.documentElement.lang = lng;

  if (isSupportedLanguage(lng)) {
    void loadCatalog(lng);
  }
});

export default i18n;
